import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { adminUsersAPI } from '../../../../services/admin-users-api.service';
import { signupRequestsAPI } from '../../../../services/signup-requests-api.service';
import { useErrorHandler } from '../../../../hooks/useErrorHandler';
import type {
  AdminUser,
  AdminUserSearchField,
  AdminUserSearchFormState,
  AdminUserTableRow,
} from '../../../../types/admin-users';
import type { SignupRequest } from '../../../../types/signup-requests';
import {
  ACCOUNT_STATUS,
  DISABLED_STATUSES,
  normalizeStatus,
} from '../../../../constants/admin-users.constants';

const DEFAULT_SEARCH: AdminUserSearchFormState = {
  searchValue: '',
  searchField: 'emp_id',
};

interface PaginationState {
  page: number;
  pageSize: number;
  totalItems: number;
}

interface UseAdminUsersReturn {
  isLoading: boolean;
  rows: AdminUserTableRow[];
  pagination: PaginationState;
  searchForm: AdminUserSearchFormState;
  setSearchField: (field: AdminUserSearchFormState['searchField']) => void;
  setSearchValue: (value: string) => void;
  applySearch: (overrides?: Partial<AdminUserSearchFormState>) => Promise<void>;
  clearSearch: () => Promise<void>;
  refresh: () => Promise<void>;
  setPage: (page: number) => Promise<void>;
  setPageSize: (pageSize: number) => Promise<void>;
}

function formatDate(value?: string): string {
  if (!value) {
    return '--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
}

export function mapUserToRow(user: AdminUser): AdminUserTableRow {
  const profile = user.user_profile ?? ({} as AdminUser['user_profile']);
  const role = profile.roles ?? ({} as AdminUser['user_profile']['roles']);

  const roleStatus =
    role.role_status ??
    (role.role_name && role.role_name.toLowerCase() === 'superadmin'
      ? 'Yes'
      : 'No');

  // Normalize so lowercase 'locked' and both disabled spellings (INACTIVE/DEACTIVE)
  // map to the correct action. Re-activating a disabled OR locked user sends 'ACTIVE'
  // (the value the backend updateUserStatus endpoint accepts).
  const normalizedStatus = normalizeStatus(user.status);
  let targetStatus = 'ACTIVE';
  let statusActionLabel = 'Activate';

  if (normalizedStatus === ACCOUNT_STATUS.ACTIVE) {
    targetStatus = 'INACTIVE';
    statusActionLabel = 'Deactivate';
  } else if (normalizedStatus === ACCOUNT_STATUS.LOCKED) {
    targetStatus = 'ACTIVE';
    statusActionLabel = 'Unlock';
  } else if (DISABLED_STATUSES.has(normalizedStatus)) {
    targetStatus = 'ACTIVE';
    statusActionLabel = 'Activate';
  }


  // For signup requests, user.id is set directly from request.id in mapSignupRequestToRow
  // For regular users, use profile.user_id as before
  const userId = (user as any).id ?? profile.user_id ?? user.email ?? '';
  return {
    id: String(userId),
    email: String(user.email ?? ''),
    mobile: String(profile.mobile ?? '--'),
    empId: String(profile.emp_id ?? '--'),
    name: String(profile.name ?? '--'),
    deptCode: String(profile.dept_code ?? '--'),
    roleStatus: String(roleStatus ?? '--'),
    roleName: String(role.display_name ?? role.role_name ?? '--'),
    createdAt: String(formatDate(profile.createdAt) ?? '--'),
    createdBy: String(profile.created_by ?? '--'),
    status: String(user.status ?? '--'),
    nextStatus: String(targetStatus ?? 'ACTIVE'),
    raw: {
      ...user,
      user_profile: {
        ...profile,
        roles: {
          ...role,
          role_status: roleStatus,
        },
      },
      user_status: statusActionLabel,
    } as AdminUser & { user_status: string },
  };
}

/**
 * Maps a signup request to the AdminUserTableRow format
 * so it can be displayed in the same table as regular users
 */
function mapSignupRequestToRow(request: SignupRequest): AdminUserTableRow {
  const profile = request.profile ?? ({} as SignupRequest['profile']);
  const role = profile.roles ?? ({} as NonNullable<SignupRequest['profile']['roles']>);

  return {
    id: String(request.id),
    email: String(request.email ?? ''),
    mobile: String(profile.mobile ?? '--'),
    empId: String(profile.emp_id ?? '--'),
    name: String(profile.name ?? '--'),
    deptCode: '--', // Signup requests don't have dept_code
    roleStatus: 'No',
    roleName: String(role.display_name ?? 'Pending Approval'),
    createdAt: String(formatDate(request.createdAt) ?? '--'),
    createdBy: '--', // Self-registered
    status: 'PENDING_APPROVAL',
    nextStatus: 'ACTIVE',
    raw: {
      id: request.id,
      email: request.email,
      status: 'PENDING_APPROVAL',
      user_profile: {
        user_id: request.id,
        emp_id: profile.emp_id,
        mobile: profile.mobile,
        name: profile.name,
        dept_code: undefined,
        createdAt: request.createdAt,
        created_by: undefined,
        reason_for_request: profile.reason_for_request,
        roles: {
          role_name: role.role_name ?? 'pending_approval',
          display_name: role.display_name ?? 'Pending Approval',
          role_status: 'No',
        },
      },
      user_status: 'Approve',
      // Flag to identify this is a signup request (for approve/reject actions)
      _isSignupRequest: true,
    } as unknown as AdminUser & { user_status: string; _isSignupRequest: boolean },
  };
}

export function useAdminUsers(): UseAdminUsersReturn {
  const { handleError } = useErrorHandler();
  const [isLoading, setIsLoading] = useState(false);
  const [rawUsers, setRawUsers] = useState<AdminUser[]>([]);
  const [searchForm, setSearchForm] =
    useState<AdminUserSearchFormState>(DEFAULT_SEARCH);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 50,
    totalItems: 0,
  });

  const abortRef = useRef<AbortController | null>(null);

  const cancelInFlight = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  };

const fetchUsers = useCallback(
    async (
      page = pagination.page,
      pageSize = pagination.pageSize,
      overrides?: Partial<AdminUserSearchFormState>
    ) => {
      cancelInFlight();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsLoading(true);

      try {
        const effectiveField = overrides?.searchField ?? searchForm.searchField;
        const effectiveValue = (overrides?.searchValue ?? searchForm.searchValue)?.trim();
        const hasSearch = effectiveValue.length > 0;

        // Fetch regular users
        const usersPromise = hasSearch
          ? adminUsersAPI.searchUsers({
              field: effectiveField as AdminUserSearchField,
              value: effectiveValue,
              signal: controller.signal,
            })
          : adminUsersAPI.getUsers({
              pageno: page,
              limit: pageSize,
              signal: controller.signal,
            });

        // Only fetch signup requests on page 1 without search
        const shouldFetchSignupRequests = page === 1 && !hasSearch;
        const signupPromise = shouldFetchSignupRequests
          ? signupRequestsAPI.getSignupRequests({ signal: controller.signal })
          : Promise.resolve(null);

        // Fetch both in parallel
        const [usersResponse, signupResponse] = await Promise.all([
          usersPromise,
          signupPromise,
        ]);

        const userRows = usersResponse?.data?.rows ?? [];
        const total = usersResponse?.data?.count ?? userRows.length;

        // Map signup requests to table rows (only on page 1)
        const signupRows = signupResponse?.data?.requests ?? [];
        const mappedSignupRows = signupRows.map(mapSignupRequestToRow);

        // Combine: signup requests first (as AdminUser-like objects), then regular users
        // The sort in useMemo will ensure PENDING_APPROVAL stays at top
        const combinedUsers = shouldFetchSignupRequests
          ? [...mappedSignupRows.map(row => row.raw as AdminUser), ...userRows]
          : userRows;

        setRawUsers(combinedUsers);
        setPagination({
          page,
          pageSize,
          totalItems: total + (shouldFetchSignupRequests ? signupRows.length : 0),
        });
      } catch (error) {
        if ((error as any)?.name === 'CanceledError') {
          return;
        }

        handleError(error, {
          component: 'useAdminUsers',
          action: 'fetchUsers',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [handleError, pagination.page, pagination.pageSize, searchForm]
  );

  useEffect(() => {
    fetchUsers();
    return cancelInFlight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => {
    const mappedRows = rawUsers.map(mapUserToRow);
    // Sort rows so PENDING_APPROVAL users appear first (signup requests needing attention)
    return mappedRows.sort((a, b) => {
      const aIsPending = a.status === 'PENDING_APPROVAL' ? 0 : 1;
      const bIsPending = b.status === 'PENDING_APPROVAL' ? 0 : 1;
      return aIsPending - bIsPending;
    });
  }, [rawUsers]);

const applySearch = useCallback(
    async (overrides?: Partial<AdminUserSearchFormState>) => {
      if (overrides) {
        setSearchForm((prev) => ({
          ...prev,
          ...overrides,
        }));
        await fetchUsers(1, pagination.pageSize, overrides);
      } else {
        await fetchUsers(1, pagination.pageSize);
      }
    },
    [fetchUsers, pagination.pageSize]
  );

const clearSearch = useCallback(async () => {
    setSearchForm(DEFAULT_SEARCH);
    await fetchUsers(1, pagination.pageSize, DEFAULT_SEARCH);
  }, [fetchUsers, pagination.pageSize]);

  const setSearchField = useCallback(
    (field: AdminUserSearchFormState['searchField']) => {
      setSearchForm((prev) => ({
        ...prev,
        searchField: field,
      }));
    },
    []
  );

  const setSearchValue = useCallback((value: string) => {
    setSearchForm((prev) => ({
      ...prev,
      searchValue: value,
    }));
  }, []);

  const setPage = useCallback(
    async (page: number) => {
      await fetchUsers(page, pagination.pageSize);
    },
    [fetchUsers, pagination.pageSize]
  );

  const setPageSize = useCallback(
    async (pageSize: number) => {
      await fetchUsers(1, pageSize);
    },
    [fetchUsers]
  );

  const refresh = useCallback(async () => {
    await fetchUsers(pagination.page, pagination.pageSize);
  }, [fetchUsers, pagination.page, pagination.pageSize]);

  return {
    isLoading,
    rows,
    pagination,
    searchForm,
    setSearchField,
    setSearchValue,
    applySearch,
    clearSearch,
    refresh,
    setPage,
    setPageSize,
  };
}
